/**
 * THE ONE WORKSPACE SEAM THE BOOT PATH USES (launch-marketing-pages design M2).
 *
 * The boot-path modules (the auth store first) reach the workspace only through
 * the adapter the workspace registers here, never by importing a
 * workspace store, so a public page boots without the workspace's module
 * graph. It imports nothing from the app (the `services/sessionToken.js`
 * rule), so every boot-path module can import it without a cycle.
 *
 * The workspace registers in setup and stays registered after it unmounts
 * (`composables/useWorkspaceAdapterRegistration.js`): its stores outlive the
 * mount, so what a guest built is still there to merge a landing into. An
 * adapter is therefore registered once a workspace has mounted in this page.
 * With none:
 *   - a reset, a panel change or a door change has nothing to act on, and is a
 *     no-op (`workspaceAdapter()?.resetState()`);
 *   - a landing goes onto the address instead (`utils/workspaceRoute.js#landingRoute`),
 *     and the workspace that mounts reads its `?state=` as a page load does;
 *   - the post-adoption reads wait: `runAfterSignIn` marks them pending, and
 *     the next registration runs them once.
 *
 * Every call is synchronous. The sign-out and sign-in resets run in a fixed
 * order on one tick, so nothing here ever waits on an import or a mount.
 */

/**
 * THE CLOSED METHOD SET (design M2's table). A method is added here and in the
 * table together, never at a call site.
 *
 * @typedef {object} WorkspaceAdapter
 * @property {() => void} resetState
 *   `unifiedRouter.resetWorkspaceState()`: sign-out (first, synchronously) and
 *   an existing member's sign-in.
 * @property {() => void} resetPanels
 *   `validationsActive = false`, `secondaryPanel.setAuthenticate()`,
 *   `secondaryPanel.revertPanel()`: sign-out, after `resetState`.
 * @property {(workspaceState: object) => void} applyLanding
 *   `unifiedRouter.applyWorkspaceState(workspaceState)`: a landing in place,
 *   before its `router.replace`.
 * @property {() => (Promise<void>|void)} afterSignIn
 *   `selectedTaskList = null`, `tasksStore.fetchLists()`: every whoami
 *   adoption, guests included (through `runAfterSignIn`).
 * @property {(open: boolean) => void} setDoorOpen
 *   `secondaryPanel.setAuthenticate(open)`: the sign-in entry on the desktop
 *   workspace, and a registration closing the door.
 * @property {(tab: string) => void} openTab
 *   `unifiedRouter.navigateToHorizontalTab(tab)`: a notification's "View hub".
 */

/** @type {WorkspaceAdapter|null} */
let registered = null;

// Set by an adoption that found no workspace registered; spent by the next
// registration.
let afterSignInPending = false;

/**
 * Register a mounting workspace's adapter, replacing any earlier one. A
 * post-adoption read that waited for a workspace runs now, once.
 *
 * @param {WorkspaceAdapter} adapter
 * @returns {() => void} the unregister, which leaves a newer registration in place
 */
export function registerWorkspaceAdapter(adapter) {
  registered = adapter;

  if (afterSignInPending) {
    afterSignInPending = false;
    // Nobody awaits this run, so its failure is logged rather than left
    // unhandled; the adoption that asked for it has long resolved.
    Promise.resolve(adapter.afterSignIn()).catch((error) => {
      console.warn("[WorkspaceAdapter] The post-sign-in reads failed:", error);
    });
  }

  return () => {
    if (registered === adapter) registered = null;
  };
}

/**
 * The page's workspace adapter, or null when no workspace has mounted in this
 * page.
 *
 * @returns {WorkspaceAdapter|null}
 */
export function workspaceAdapter() {
  return registered;
}

/**
 * The post-adoption reads: run them on the page's workspace and hand back
 * their promise, or, with none registered, mark them for the next registration.
 *
 * @returns {Promise<void>|void}
 */
export function runAfterSignIn() {
  if (registered) return registered.afterSignIn();
  afterSignInPending = true;
  return undefined;
}

export function __resetWorkspaceAdapterForTests() {
  registered = null;
  afterSignInPending = false;
}
